import React, { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { loadCourtDates } from '../common/helper_functions.js';
import { useUser } from './UserContext';
import { toast } from '../common/toast';
import logger from '../utils/logger';

const CourtDateContext = createContext();

export const CourtDateProvider = ({ children }) => {
  const { user, loading: authLoading } = useUser();
  const [courtDates, setCourtDates] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCourtDatesForUser = async () => {
    // Wait for auth to complete before loading court dates
    if (window.location.pathname === '/snap' || !user || authLoading) {
      if(courtDates.length > 0) {
        setCourtDates([]);
      }
      return;
    }

    let showSettledCourtdates = false;
    try {
      const storedSettings = localStorage.getItem('advancedSettings');
      if (storedSettings) {
        const parsed = JSON.parse(storedSettings);
        showSettledCourtdates = parsed?.showSettledCourtdates || false;
      }
    } catch (error) {
      logger.error('Error reading advancedSettings:', error);
    }

    setLoading(true);
    try {
      const response = await loadCourtDates(user, showSettledCourtdates);
      setCourtDates(response);
    } catch (error) {
      logger.error('Error loading court dates:', error);
      if(courtDates.length > 0) {
        setCourtDates([]);
      }
      toast.error('Failed to load court dates. Please check your connection and try again.', {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourtDatesForUser();
  }, [user?.email, user?.userId, authLoading]);

  const contextValue = useMemo(() => ({ courtDates, loading, reloadCourtDates: loadCourtDatesForUser }), [courtDates, loading]);

  return (
    <CourtDateContext.Provider value={contextValue}>
      {children}
    </CourtDateContext.Provider>
  );
};

export const useCourtDates = () => {
  const context = useContext(CourtDateContext);
  if (!context) {
    throw new Error('useCourtDates must be used within a CourtDateProvider');
  }
  return context;
}; 